import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';

import type { Session, User } from '@supabase/supabase-js';

import { supabase } from './client';
import { googleSignInManager } from '@/lib/auth';
import { localAccountDataPurger } from '@/lib/managers/LocalAccountDataPurger';
import { generateAPIUrl } from '@/utils';

type SupabaseAuthContextValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithPassword: (email: string, password: string) => ReturnType<
    typeof supabase.auth.signInWithPassword
  >;
  signUpWithPassword: (email: string, password: string) => Promise<SignUpResult>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => ReturnType<typeof supabase.auth.signOut>;
  refreshSession: () => Promise<void>;
};

export type SignUpResult = {
  error: Error | null;
  data: { user: { email_confirmed_at: string | null } | null };
};

const SupabaseAuthContext = createContext<SupabaseAuthContextValue | undefined>(undefined);

export const SupabaseProvider = ({ children }: PropsWithChildren) => {
  const [initializing, setInitializing] = useState(true);
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    const clientId = process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID || process.env.GOOGLE_CLIENT_ID;
    if (clientId) {
      googleSignInManager.configure(clientId);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let resolved = false;

    const finishInit = (sessionValue: Session | null) => {
      if (!isMounted || resolved) return;
      resolved = true;
      setSession(sessionValue);
      setInitializing(false);
    };

    const safetyTimeout = setTimeout(() => {
      if (!resolved) {
        console.warn('[SupabaseProvider] Session fetch timed out after 5s, redirecting to sign-in');
        finishInit(null);
      }
    }, 5000);

    supabase.auth
      .getSession()
      .then(({ data, error }) => {
        if (error || !data.session) {
          console.warn('[SupabaseProvider] Initial session invalid or missing, clearing stale auth state');
          supabase.auth.signOut().catch(() => {}); // fire-and-forget cleanup
          finishInit(null);
        } else {
          finishInit(data.session);
        }
      })
      .catch((err) => {
        console.error('[SupabaseProvider] Failed to get session:', err);
        finishInit(null);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if ((event === 'TOKEN_REFRESHED' || event === 'SIGNED_OUT') && !nextSession) {
        console.warn(`[SupabaseProvider] Auth event "${event}" with null session, clearing stale state`);
        setSession(null);
        return;
      }
      setSession(nextSession);
    });

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Validates the cached JWT against Supabase's /auth/v1/user endpoint.
   * Network errors are treated as "unknown" and ignored. Auth errors mean
   * the user was deleted (or otherwise invalidated server-side) while we
   * were caching a stale JWT — purge local per-account data so the next
   * sign-in starts fresh (calendar onboarding flow, no stale calendar
   * selection), then sign out so the app routes to /sign-in.
   */
  const validateSessionWithServer = useCallback(async () => {
    const { data: currentSession } = await supabase.auth.getSession();
    const token = currentSession.session?.access_token;
    if (!token) {
      return;
    }

    try {
      const { error } = await supabase.auth.getUser(token);
      if (error) {
        console.warn(
          '[SupabaseProvider] Server rejected cached session, signing out:',
          error.message,
        );
        const purgedUserId = currentSession.session?.user?.id ?? null;
        await localAccountDataPurger.purge(purgedUserId);
        setSession(null);
        await supabase.auth.signOut().catch(() => {});
      }
    } catch (networkError) {
      console.warn(
        '[SupabaseProvider] Session validation skipped (network error):',
        networkError,
      );
    }
  }, []);

  useEffect(() => {
    const handleAppStateChange = (state: AppStateStatus) => {
      if (state === 'active') {
        supabase.auth.startAutoRefresh();
        // Server may have invalidated this session while we were backgrounded
        // (e.g. account deletion from another device). Verify before trusting
        // the cached JWT.
        void validateSessionWithServer();
      } else if (state === 'background') {
        supabase.auth.stopAutoRefresh();
      }
    };

    const listener = AppState.addEventListener('change', handleAppStateChange);
    supabase.auth.startAutoRefresh();
    void validateSessionWithServer();

    return () => {
      listener.remove();
      supabase.auth.stopAutoRefresh();
    };
  }, [validateSessionWithServer]);

  const signInWithGoogle = async (): Promise<{ error: Error | null }> => {
    try {
      const result = await googleSignInManager.signIn();

      if (!result.success) {
        if (result.cancelled) {
          return { error: null };
        }
        return { error: new Error(result.error) };
      }

      const { error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: result.idToken,
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Unknown error') };
    }
  };

  const value = useMemo<SupabaseAuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading: initializing,
      signInWithPassword: async (email: string, password: string) => {
        return supabase.auth.signInWithPassword({ email, password });
      },
      signUpWithPassword: async (email: string, password: string): Promise<SignUpResult> => {
        try {
          const response = await fetch(generateAPIUrl('/api/auth/signup-with-password'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          const payload = await response.json().catch(() => null) as
            | { ok: true; needsConfirmation: boolean; message: string }
            | { ok: false; code: string; error: string }
            | null;

          if (!response.ok || !payload || payload.ok === false) {
            const message = (payload && 'error' in payload && payload.error)
              || `Signup failed (${response.status})`;
            return { error: new Error(message), data: { user: null } };
          }

          // Email confirmation is the only supported flow for password signup.
          // The endpoint always requires confirmation regardless of payload.needsConfirmation.
          return {
            error: null,
            data: { user: { email_confirmed_at: null } },
          };
        } catch (err) {
          return {
            error: err instanceof Error ? err : new Error('Network error during signup'),
            data: { user: null },
          };
        }
      },
      signInWithGoogle,
      signOut: async () => {
        setSession(null);
        const result = await supabase.auth.signOut().catch((err) => {
          console.warn('[SupabaseProvider] signOut network call failed:', err);
          return { error: err };
        });
        return result;
      },
      refreshSession: async () => {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) {
          throw error;
        }
        setSession(data.session);
      },
    }),
    [initializing, session],
  );

  return (
    <SupabaseAuthContext.Provider value={value}>{children}</SupabaseAuthContext.Provider>
  );
};

export const useSupabaseAuth = () => {
  const context = useContext(SupabaseAuthContext);
  if (!context) {
    throw new Error('useSupabaseAuth must be used within a SupabaseProvider.');
  }
  return context;
};
